import { useEffect, useState } from "react";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";

const useStyles = makeStyles({
  root: {
    position: "fixed",
    inset: 0,
    zIndex: 50,
    display: "flex",
    flexDirection: "column",
    backgroundColor: BRAND.ink,
    backgroundImage: `linear-gradient(135deg, ${BRAND.ink} 0%, ${BRAND.purple} 60%, ${BRAND.ink} 100%)`,
    color: BRAND.white,
    fontFamily: BRAND.fontPrimary,
  },
  topBar: {
    display: "flex",
    justifyContent: "flex-end",
    padding: "16px",
  },
  skipBtn: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: "rgba(255,255,255,0.06)",
    backgroundImage: "none",
    color: "rgba(255,255,255,0.85)",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    fontWeight: 600,
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.border("1px", "solid", "rgba(255,255,255,0.2)"),
    ...shorthands.borderRadius("4px"),
    ":hover": {
      backgroundColor: "rgba(255,255,255,0.12)",
    },
  },
  stage: {
    flexGrow: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    paddingLeft: "16px",
    paddingRight: "16px",
    paddingBottom: "80px",
  },
  pageOneWrap: { textAlign: "center" },
  eyebrow: {
    marginBottom: "12px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.72rem",
    letterSpacing: "0.4em",
    textTransform: "uppercase",
    color: "rgba(247,168,35,0.85)",
    animationDuration: "600ms",
    animationTimingFunction: "ease-out",
    animationFillMode: "both",
    animationName: { from: { opacity: 0 }, to: { opacity: 1 } },
  },
  hero: {
    fontFamily: BRAND.fontAccent,
    color: BRAND.gold,
    fontSize: "clamp(2.5rem, 9vw, 6rem)",
    lineHeight: 1.05,
    fontWeight: 400,
    margin: 0,
    animationDuration: "700ms",
    animationTimingFunction: "ease-out",
    animationFillMode: "both",
    animationName: {
      from: { opacity: 0, transform: "translateY(12px)" },
      to: { opacity: 1, transform: "translateY(0)" },
    },
  },
  heroSub: {
    marginTop: "8px",
    fontFamily: BRAND.fontAccent,
    color: "rgba(255,255,255,0.95)",
    fontSize: "clamp(1.6rem, 5vw, 2.4rem)",
    animationDuration: "1100ms",
    animationDelay: "300ms",
    animationTimingFunction: "ease-out",
    animationFillMode: "both",
    animationName: {
      from: { opacity: 0, transform: "translateY(12px)" },
      to: { opacity: 1, transform: "translateY(0)" },
    },
  },
  pageTwo: {
    maxWidth: "740px",
    textAlign: "center",
    fontFamily: BRAND.fontAccent,
    fontSize: "clamp(1.4rem, 3.4vw, 2rem)",
    lineHeight: 1.45,
    color: "rgba(255,255,255,0.95)",
    animationDuration: "700ms",
    animationTimingFunction: "ease-out",
    animationFillMode: "both",
    animationName: {
      from: { opacity: 0, transform: "translateY(12px)" },
      to: { opacity: 1, transform: "translateY(0)" },
    },
  },
  bold: { color: BRAND.gold, fontWeight: 700 },
  tilesWrap: {
    width: "100%",
    maxWidth: "1024px",
    marginLeft: "auto",
    marginRight: "auto",
    paddingTop: "20px",
    paddingBottom: "20px",
    paddingLeft: "20px",
    paddingRight: "20px",
    backgroundColor: BRAND.purple,
    backgroundImage: `linear-gradient(135deg, ${BRAND.purple} 0%, #3a1c52 100%)`,
    ...shorthands.borderRadius("10px"),
    boxShadow: "0 24px 48px rgba(0,0,0,0.3)",
  },
  tilesHeading: {
    marginTop: 0,
    marginBottom: "20px",
    color: BRAND.gold,
    fontFamily: BRAND.fontAccent,
    fontWeight: 400,
    fontSize: "clamp(1.1rem, 3.6vw, 1.6rem)",
    lineHeight: 1.2,
  },
  tileRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "12px",
    "@media (min-width: 768px)": {
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: "16px",
    },
  },
  tile: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "space-between",
    color: BRAND.white,
    boxShadow: "0 6px 12px rgba(0,0,0,0.25)",
    minHeight: "180px",
    paddingTop: "16px",
    paddingBottom: "16px",
    paddingLeft: "14px",
    paddingRight: "14px",
    textAlign: "center",
    backgroundColor: BRAND.pillarBlue,
    ...shorthands.borderRadius("8px"),
  },
  tileNumber: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "26px",
    height: "26px",
    fontWeight: 700,
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    fontSize: "0.85rem",
    boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
    ...shorthands.borderRadius("999px"),
  },
  tileLabel: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.92rem",
    fontWeight: 600,
    lineHeight: 1.25,
  },
  pageThreeNote: {
    maxWidth: "640px",
    marginLeft: "auto",
    marginRight: "auto",
    marginTop: "20px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    textAlign: "center",
    color: "rgba(255,255,255,0.7)",
  },
  footer: {
    position: "absolute",
    insetInlineStart: 0,
    insetInlineEnd: 0,
    bottom: 0,
    display: "flex",
    justifyContent: "center",
    paddingBottom: "24px",
  },
  footerInner: {
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
  },
  dots: { display: "flex", alignItems: "center", columnGap: "6px" },
  dot: {
    height: "6px",
    width: "24px",
    backgroundColor: "rgba(255,255,255,0.25)",
    transitionProperty: "background-color",
    transitionDuration: "200ms",
    ...shorthands.borderRadius("999px"),
  },
  dotActive: { backgroundColor: BRAND.gold },
  weiterBtn: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.gold,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    fontWeight: 700,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "20px",
    paddingRight: "20px",
    ...shorthands.border("1px", "solid", BRAND.gold),
    ...shorthands.borderRadius("6px"),
    ":hover": {
      backgroundColor: "#e89d20",
      ...shorthands.borderColor("#e89d20"),
    },
  },
});

const PAGE_DURATIONS_MS = [3000, 6000, 6000];

const TileIcon = {
  chart: () => (
    <svg width="40" height="28" viewBox="0 0 56 36" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 32h48" />
      <rect x="10" y="20" width="6" height="12" />
      <rect x="22" y="14" width="6" height="18" />
      <rect x="34" y="8" width="6" height="24" />
      <rect x="46" y="22" width="6" height="10" />
    </svg>
  ),
  euro: () => (
    <svg width="40" height="28" viewBox="0 0 56 36" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="28" cy="18" r="14" />
      <path d="M36 12a10 10 0 1 0 0 12" />
      <path d="M18 16h12M18 22h12" />
    </svg>
  ),
  people: () => (
    <svg width="40" height="28" viewBox="0 0 56 36" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="18" cy="14" r="5" />
      <path d="M8 30c0-6 4-10 10-10s10 4 10 10" />
      <circle cx="38" cy="14" r="5" />
      <path d="M28 30c0-6 4-10 10-10s10 4 10 10" />
    </svg>
  ),
};

type Props = {
  companyName: string;
  onComplete: () => void;
};

const KennzahlenWelcomeFlow = ({ companyName, onComplete }: Props) => {
  const styles = useStyles();
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (page > 2) return;
    const t = window.setTimeout(() => {
      if (page === 2) onComplete();
      else setPage((p) => p + 1);
    }, PAGE_DURATIONS_MS[page]);
    return () => window.clearTimeout(t);
  }, [page, onComplete]);

  const skip = () => {
    if (page === 2) onComplete();
    else setPage((p) => p + 1);
  };
  const skipAll = () => onComplete();

  return (
    <div className={styles.root}>
      <div className={styles.topBar}>
        <button type="button" onClick={skipAll} className={styles.skipBtn}>
          Direkt zum Formular →
        </button>
      </div>
      <div className={styles.stage}>
        {page === 0 && (
          <div className={styles.pageOneWrap}>
            <div className={styles.eyebrow}>Kennzahlen-Erfassung</div>
            <h1 className={styles.hero}>Ihre Zahlen</h1>
            <div className={styles.heroSub}>
              {companyName || "Ihr Unternehmen"}
            </div>
          </div>
        )}
        {page === 1 && (
          <div className={styles.pageTwo}>
            Wir benötigen die <span className={styles.bold}>Basis-Kennzahlen</span>{" "}
            Ihres Unternehmens — <span className={styles.bold}>Umsatz</span>,{" "}
            <span className={styles.bold}>Aufwendungen</span> und{" "}
            <span className={styles.bold}>Mitarbeiter</span> — pro Geschäftsjahr.
          </div>
        )}
        {page === 2 && (
          <div className={styles.tilesWrap}>
            <h2 className={styles.tilesHeading}>
              Drei Bereiche pro Geschäftsjahr
            </h2>
            <div className={styles.tileRow}>
              <div className={styles.tile}>
                <span className={styles.tileNumber}>1</span>
                <TileIcon.chart />
                <span className={styles.tileLabel}>Basis-Kennzahlen</span>
              </div>
              <div className={styles.tile}>
                <span className={styles.tileNumber}>2</span>
                <TileIcon.euro />
                <span className={styles.tileLabel}>Risiko & Kosten</span>
              </div>
              <div className={styles.tile}>
                <span className={styles.tileNumber}>3</span>
                <TileIcon.people />
                <span className={styles.tileLabel}>Mitarbeiter</span>
              </div>
            </div>
            <p className={styles.pageThreeNote}>
              Sie können jederzeit zwischenspeichern und später fortfahren.
            </p>
          </div>
        )}
      </div>
      <div className={styles.footer}>
        <div className={styles.footerInner}>
          <div className={styles.dots}>
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className={mergeClasses(styles.dot, (i === page) && styles.dotActive)}
              />
            ))}
          </div>
          <button type="button" onClick={skip} className={styles.weiterBtn}>
            {page === 2 ? "Zum Formular" : "Weiter"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default KennzahlenWelcomeFlow;
